import imageCompression from 'browser-image-compression'

export type ImageUploadPreset = 'product' | 'banner' | 'review'

const PRESETS: Record<
  ImageUploadPreset,
  { maxWidthOrHeight: number; maxSizeMB: number; initialQuality: number }
> = {
  product: { maxWidthOrHeight: 1200, maxSizeMB: 0.5, initialQuality: 0.8 },
  banner: { maxWidthOrHeight: 1600, maxSizeMB: 0.8, initialQuality: 0.82 },
  review: { maxWidthOrHeight: 1200, maxSizeMB: 0.4, initialQuality: 0.78 },
}

const IMAGE_EXT_TYPES = new Set([
  'jpg',
  'jpeg',
  'png',
  'webp',
  'gif',
  'heic',
  'heif',
])

/** True for HEIC/HEIF — common on iPhone; browsers cannot display or compress these directly. */
export function isHeicImageFile(file: File): boolean {
  const type = file.type.toLowerCase()
  if (type === 'image/heic' || type === 'image/heif') return true
  const ext = file.name.split('.').pop()?.toLowerCase()
  return ext === 'heic' || ext === 'heif'
}

export function isSupportedImageFile(file: File): boolean {
  if (file.type.startsWith('image/')) return true
  const ext = file.name.split('.').pop()?.toLowerCase()
  return IMAGE_EXT_TYPES.has(ext ?? '')
}

function isOptimizableImage(file: File): boolean {
  if (!isSupportedImageFile(file)) return false
  if (file.type === 'image/gif') return false
  const ext = file.name.split('.').pop()?.toLowerCase()
  if (ext === 'gif') return false
  return true
}

function fileWithWebpName(file: File, blob: Blob): File {
  const baseName = file.name.replace(/\.[^.]+$/, '') || 'image'
  const type = blob.type || 'image/webp'
  const ext = type === 'image/jpeg' ? 'jpg' : 'webp'
  return new File([blob], `${baseName}.${ext}`, { type, lastModified: Date.now() })
}

async function convertHeicToJpeg(file: File): Promise<File> {
  const { default: heic2any } = await import('heic2any')
  const result = await heic2any({
    blob: file,
    toType: 'image/jpeg',
    quality: 0.92,
  })
  const blob = Array.isArray(result) ? result[0] : result
  if (!(blob instanceof Blob)) {
    throw new Error('HEIC conversion failed')
  }
  const baseName = file.name.replace(/\.[^.]+$/, '') || 'image'
  return new File([blob], `${baseName}.jpg`, {
    type: 'image/jpeg',
    lastModified: Date.now(),
  })
}

/** Normalize iPhone HEIC/HEIF to a browser-friendly JPEG before further processing. */
export async function normalizeImageFileForUpload(file: File): Promise<File> {
  if (!isHeicImageFile(file)) return file
  return convertHeicToJpeg(file)
}

/** Resize and compress storefront images before Firebase upload. */
export async function optimizeImageForUpload(
  file: File,
  preset: ImageUploadPreset = 'product'
): Promise<File> {
  let input = file
  if (isHeicImageFile(file)) {
    input = await convertHeicToJpeg(file)
  }

  if (!isOptimizableImage(input)) return input

  const { maxWidthOrHeight, maxSizeMB, initialQuality } = PRESETS[preset]

  try {
    const compressed = await imageCompression(input, {
      maxWidthOrHeight,
      maxSizeMB,
      initialQuality,
      useWebWorker: true,
      fileType: 'image/webp',
    })
    return fileWithWebpName(input, compressed)
  } catch {
    if (input !== file) return input
    return file
  }
}

export function replacePathExtension(path: string, file: File): string {
  const ext = file.name.split('.').pop()?.toLowerCase() || 'webp'
  const dot = path.lastIndexOf('.')
  if (dot === -1) return `${path}.${ext}`
  return `${path.slice(0, dot)}.${ext}`
}
